import React, { useMemo } from "react";

/**
 * FormatSelector — lista formati nel pannello sinistro del Social Studio,
 * raggruppati in 2 sezioni con header per ridurre il "muro" visivo:
 *
 *   QUOTIDIANO   → contenuti regolari per nutrire il feed
 *                  (Post · Carosello · Story · Reel · Prima/Dopo)
 *   PER OCCASIONE → contenuti per momenti chiave del listing
 *                  (Nuovo annuncio · Open House · Ribasso · Venduto · Brochure)
 *
 * Riceve `formats[]` (con `group: "quotidiano" | "occasione"`), il formato
 * attivo, il manifest (per check disponibilità) e un setter `onSelect`.
 * Pure presentation: nessuna business logic.
 */
const GROUP_META = {
  quotidiano: { label: "Quotidiano", hint: "per il feed di tutti i giorni — niente etichette speciali, ottimo per post regolari" },
  occasione: { label: "Per occasione", hint: "momenti chiave del listing — \"Nuovo\", \"Open House\", \"Ribasso\", \"Venduto\" — con badge ben visibile" },
};

export default function FormatSelector({ formats, active, manifest, photoCount, onSelect }) {
  const grouped = useMemo(() => {
    const out = { quotidiano: [], occasione: [] };
    formats.forEach((f) => {
      const g = f.group || "quotidiano";
      (out[g] || (out[g] = [])).push(f);
    });
    return out;
  }, [formats]);

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([groupKey, items]) => {
        if (!items?.length) return null;
        const meta = GROUP_META[groupKey] || { label: groupKey };
        return (
          <section key={groupKey}>
            <div className="flex items-baseline justify-between mb-2 px-0.5">
              <h4 className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E]">
                {meta.label}
              </h4>
              {meta.hint && (
                <span className="text-[10px] text-[#9A968B] italic">
                  {meta.hint}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {items.map(({ key, label, sublabel, icon: Icon, isStatic, isNew }) => {
                const isActive = active === key;
                // iter v28 r100 — Aggiunto `occ_prezzo_nuovo` (iter precedente
                // l'aveva creato ma non era cliccabile perché qui mancava
                // dall'elenco delle occasioni dinamiche).
                const isOccasionDynamic = key === "occ_open_house" || key === "occ_venduto" || key === "occ_prezzo_nuovo";
                const isAvail = isStatic
                  ? true
                  : isOccasionDynamic
                  ? (photoCount || 0) >= 1
                  : manifest?.formats?.[key]?.available;
                return (
                  <button
                    key={key}
                    onClick={() => isAvail && onSelect(key)}
                    disabled={!isAvail}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      isActive
                        ? "border-[#7C8B6E] bg-[#F1F4ED]"
                        : "border-[#EAE7DF] hover:border-[#7C8B6E]/40 hover:bg-[#FAFAF7]"
                    } ${!isAvail ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`format-${key}`}
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center ${isActive ? "bg-[#7C8B6E] text-white" : "bg-[#F1F4ED] text-[#7C8B6E]"}`}>
                      <Icon size={17} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold text-[#1C1C1A] flex items-center gap-1.5">
                        {label}
                        {isNew && (
                          <span className="text-[8.5px] font-bold tracking-[0.18em] uppercase text-[#7C8B6E] bg-[#F1F4ED] px-1.5 py-0.5 rounded-sm leading-none">Nuovo</span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#6B6A65]">{sublabel}</div>
                    </div>
                    {key === "reel" && photoCount < 2 && (
                      <span className="text-[10px] text-[#9A968B] italic">serve 1+ foto</span>
                    )}
                    {key === "before_after" && !manifest?.formats?.before_after?.available && (
                      <span className="text-[10px] text-[#9A968B] italic" data-testid="ba-needs-staging">
                        serve una foto trasformata
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
